import {Injectable} from '@angular/core';
import {forkJoin, Observable, of} from 'rxjs';
import {
  Contact,
  ContactService,
  Customer,
  CustomerContact,
  CustomerContactService,
  CustomerService,
  CustomerSourcingService,
  DateUtils,
  InsuranceCategoryFamily,
  InsuranceService,
  InsuranceSubscription,
  InsuranceSubscriptionGroupField,
  InsuranceSubscriptionGroupSummary,
  InsuranceSubscriptionSearch,
  InsuranceSubscriptionService,
  InsuranceSubscriptionStatus,
  isSameRef,
  isValidRef,
  Ref,
  SearchResult,
  SimplePaginationUtils,
  Subscription,
  SubscriptionService,
} from '@lifeislife/lifeislife-domain';
import {CustomerContextService} from '../service/customer-context-service';
import {map, switchMap} from 'rxjs/operators';
import {DomSanitizer} from '@angular/platform-browser';
import {PensionProductPreviewModel} from './pension-product-preview-model';
import {PensionProductLegendModel} from '../products-route/pension-products-family-route/pension-product-legend-model';
import {ContactPensionProductsPreviewModel} from './contact-pension-products-preview-model';
import {CustomerFrontAppSwitches} from '../../domain/customer-front-app-switches';


@Injectable({
  providedIn: 'root',
})
export class PensionProductsPreviewService {


  constructor(
    private sourcingService: CustomerSourcingService,
    private insuranceService: InsuranceService,
    private customerService: CustomerService,
    private customerContactService: CustomerContactService,
    private contactService: ContactService,
    private insuranceSubscriptionService: InsuranceSubscriptionService,
    private customerContextService: CustomerContextService,
    private subscriptionService: SubscriptionService,
    private domSanitizer: DomSanitizer,
  ) {
  }


  createPensionProductsPreviewModels$(perContactFeature: boolean): Observable<ContactPensionProductsPreviewModel[]> {
    return this.customerContextService.activeCustomerContact$.pipe(
      // 2021-06: Display charts for each contact, but do not bother with the logged contact.
      // All customer contacts are displayed all contacts preview model
      switchMap(c => this.createContactPreviewModelsForCustomer$(c, perContactFeature)),
    );
  }

  createVestedReserveBreakDownChartModel(model: ContactPensionProductsPreviewModel): { name: string, value: number }[] {
    return model.products
      .sort((a, b) => {
        return (a.insuranceSubscriptionRef == null ? -1 : (
          a.insuranceSubscriptionRef.id < b.insuranceSubscriptionRef.id ? -1 : 1
        ));
      })
      .map(pmodel => {
        return {
          model: pmodel,
          name: pmodel.subscriptionName,
          value: pmodel.vestedReserveAmount,
        };
      });
  }

  createEstimatedPensioNAmountBreakdownChartModel(model: ContactPensionProductsPreviewModel): { name: string, value: number }[] {
    return model.products
      .sort((a, b) => {
        return a.insuranceSubscriptionRef.id < b.insuranceSubscriptionRef.id ? -1 : 1;
      })
      .map(pmodel => {
        return {
          model: pmodel,
          name: pmodel.subscriptionName,
          value: pmodel.estimatedPensionAmount,
        };
      });
  }

  createEstimatedIncomeOnTwentyYearChartModel(model: ContactPensionProductsPreviewModel): { name: string, value: number }[] {
    const models = model.products
      .sort((a, b) => {
        return (a.insuranceSubscriptionRef == null ? -1 : (
          a.insuranceSubscriptionRef.id < b.insuranceSubscriptionRef.id ? -1 : 1
        ));
      })
      .map(pmodel => {
        return {
          model: pmodel,
          name: pmodel.subscriptionName,
          value: (pmodel.estimatedPensionAmount / 20 / 12),
        };
      });
    models.push({
      name: `Pension légale`,
      value: 1000,
      model: null,
    });
    return models;
  }


  createSubscriptionLegendModel$(previewModel: PensionProductPreviewModel, color: string): Observable<PensionProductLegendModel> {
    if (previewModel == null || previewModel.insuranceSubscriptionRef == null) {
      return of(null);
    }
    return this.insuranceSubscriptionService.getInsuranceSubscription$(previewModel.insuranceSubscriptionRef).pipe(
      map(i => this.createInsuranceSubscriptionModel(previewModel, i, color)),
    );
  }

  private createInsuranceSubscriptionModel(previewModel: PensionProductPreviewModel,
                                           insuranceSubscription: InsuranceSubscription,
                                           color: string): PensionProductLegendModel {
    return {
      estimatedPensionAMount: previewModel.estimatedPensionAmount,
      estimatedPensionIncomeAmount: (previewModel.estimatedPensionAmount / 20 / 12),
      vestedReserveAmount: previewModel.vestedReserveAmount,
      colorString: color,
      insuranceSubscription: insuranceSubscription,
    };
  }

  private createContactPreviewModelsForCustomer$(customerContact: CustomerContact, perContactFeature: boolean): Observable<ContactPensionProductsPreviewModel[]> {
    if (customerContact == null) {
      return of([]);
    }
    const customerRef = customerContact.customerRef;
    if (customerRef == null) {
      return of([]);
    }
    const customerMainContactRef$ = this.customerService.getCustomer$(customerRef).pipe(
      map(c => c.mainCustomerContactRef),
    );
    const groupField = perContactFeature ? InsuranceSubscriptionGroupField.INSURED_CONTACT : InsuranceSubscriptionGroupField.CUSTOMER;
    const groupSummaries$ = this.searchCustomerSubscriptionGroupSummaries$(customerRef, groupField);
    return forkJoin([groupSummaries$, customerMainContactRef$]).pipe(
      switchMap(r => this.createContactPreviewModelsFromGroupSummaries$(customerRef, groupField, r[0], r[1])),
    );
  }

  private searchCustomerSubscriptionGroupSummaries$(customerRef: Ref<Customer>, groupField: InsuranceSubscriptionGroupField): Observable<SearchResult<InsuranceSubscriptionGroupSummary>> {
    const insuranceSubscriptionSearch = this.createInsuranceSubscriptionSearchFilter(customerRef, undefined);
    const pagination = SimplePaginationUtils.newPagination(300);
    pagination.groups = [groupField];
    return this.insuranceSubscriptionService.searchSubscriptionsGroupSummaries$(insuranceSubscriptionSearch, pagination);
  }


  private createContactPreviewModel$(contact: Contact, results: SearchResult<InsuranceSubscription>): Observable<ContactPensionProductsPreviewModel> {
    const contactName = this.contactService.getContactName(contact);
    if (results.count === 0) {
      return of({
        insuredContact: contact,
        insuredContactName: contactName,
        products: [],
        totalVestedReserveAmount: null,
        totalEstimatedPensionAmount: null,
        vestedReserveRatio: 0,
        vestedReserveWidthCss: this.domSanitizer.bypassSecurityTrustStyle(`width: 0%;`),
      } as ContactPensionProductsPreviewModel);
    }

    const subscriptionModels$List = results.list.map(sub => this.createSubscriptionModel$(sub));
    const subscriptionModels$ = subscriptionModels$List.length === 0 ? of([]) : forkJoin(subscriptionModels$List);
    return subscriptionModels$.pipe(
      map(models => this.createMainModel(contact, models)),
    );
  }

  private createCustomerPreviewModel$(results: SearchResult<InsuranceSubscription>): Observable<ContactPensionProductsPreviewModel> {
    if (results.count === 0) {
      return of({
        insuredContact: null,
        insuredContactName: null,
        products: [],
        totalVestedReserveAmount: null,
        totalEstimatedPensionAmount: null,
        vestedReserveRatio: 0,
        vestedReserveWidthCss: this.domSanitizer.bypassSecurityTrustStyle(`width: 0%;`),
      } as ContactPensionProductsPreviewModel);
    }

    const subscriptionModels$List = results.list.map(sub => this.createSubscriptionModel$(sub));
    const subscriptionModels$ = subscriptionModels$List.length === 0 ? of([]) : forkJoin(subscriptionModels$List);
    return subscriptionModels$.pipe(
      map(models => this.createMainModel(null, models)),
    );
  }

  private createSubscriptionModel$(insuranceSubscription: InsuranceSubscription) {
    const subscription$ = this.subscriptionService.getSubscription$(insuranceSubscription.subscriptionRef);
    const label$ = this.insuranceSubscriptionService.getInsuranceSubscriptionName$(insuranceSubscription);
    return forkJoin(of(insuranceSubscription), subscription$, label$).pipe(
      map(r => this.createLifeSubscriptionModel(r[0], r[1], r[2])),
    );
  }

  private createLifeSubscriptionModel(insuranceSubscription: InsuranceSubscription,
                                      subscription: Subscription, label: string): PensionProductPreviewModel {
    return {
      insuranceSubscriptionRef: {id: insuranceSubscription.id},
      subscriptionName: label,
      subscriptionInternalRef: subscription.internalReference,
      vestedReserveAmount: insuranceSubscription.vestedReserveAmount || 0,
      estimatedPensionAmount: insuranceSubscription.estimatedRetirementAmount || 0,
      estimatedPensionAmountWidthCss: null, // TO be filled later with all models
      estimatedPensionAmountWidthPercent: 0,
      lastUpdatedDate: insuranceSubscription.vestedReserveAmountUpdateDate,
    };
  }

  private createMainModel(insuredContact: Contact, models: PensionProductPreviewModel[]): ContactPensionProductsPreviewModel {
    const totalEstimatedPensionAmount = models.reduce((c, n) => c + n.estimatedPensionAmount, 0);
    const totalVestedReserveAmount = models.reduce((c, n) => c + n.vestedReserveAmount, 0);
    let lastUpdateDate: Date = null;

    for (const model of models) {
      const estimatedPensionAmount = model.estimatedPensionAmount;
      const estimatedPensionAmountRatio = estimatedPensionAmount / totalEstimatedPensionAmount;
      const estmatedPensionAmountPercentString = (estimatedPensionAmountRatio * 100).toFixed(2) + '%';
      const esimatedPensionAmountWidthCss = this.domSanitizer.bypassSecurityTrustStyle(estmatedPensionAmountPercentString);

      model.estimatedPensionAmountWidthPercent = estimatedPensionAmountRatio * 100;
      model.estimatedPensionAmountWidthCss = esimatedPensionAmountWidthCss;

      if (lastUpdateDate == null) {
        lastUpdateDate = model.lastUpdatedDate;
      } else {
        const modelUpdatedLater = DateUtils.isDistinctSortedDays(lastUpdateDate, model.lastUpdatedDate);
        lastUpdateDate = modelUpdatedLater ? model.lastUpdatedDate : lastUpdateDate;
      }
    }

    const vestedRatio = totalVestedReserveAmount / (totalEstimatedPensionAmount === 0 ? 1 : totalEstimatedPensionAmount);
    const vestedPecerntString = (vestedRatio * 100).toFixed(2) + '%';
    const vestedWidthCss = this.domSanitizer.bypassSecurityTrustStyle(vestedPecerntString);

    const contactName = insuredContact == null ? null : this.contactService.getContactName(insuredContact);

    return {
      insuredContact: insuredContact,
      insuredContactName: contactName,

      totalEstimatedPensionAmount: totalEstimatedPensionAmount,
      totalVestedReserveAmount: totalVestedReserveAmount,

      vestedReserveRatio: vestedRatio,
      vestedReserveWidthCss: vestedWidthCss,

      products: models,
      lastUpdatedDate: lastUpdateDate,
    };
  }


  private createInsuranceSubscriptionSearchFilterNoContact(customerRef: Ref<Customer>) {
    const insuranceSubscriptionSearch: InsuranceSubscriptionSearch = {
      insuranceSearch: {
        categorySearch: {
          anyFamily: [InsuranceCategoryFamily.RETIREMENT],
        },
      },
      insuranceSubscriptionStatusSearch: {
        allVisibleToCustomers: true,
        anyStatusList: [InsuranceSubscriptionStatus.ACTIVE, InsuranceSubscriptionStatus.DISCOUNTING],
      },
      subscriptionSearch: {
        archived: false,
        customerSearch: {
          exactCustomerRef: customerRef,
        },
      },
    };
    return insuranceSubscriptionSearch;
  }

  private createInsuranceSubscriptionSearchFilter(customerRef: Ref<Customer>, insuredCustomerContactRef: Ref<CustomerContact> | null) {
    const insuranceSubscriptionSearch: InsuranceSubscriptionSearch = {
      insuranceSearch: {
        categorySearch: {
          anyFamily: [InsuranceCategoryFamily.RETIREMENT],
        },
      },
      insuredCustomerContactSearch: {
        exactCustomerContactRef: insuredCustomerContactRef || undefined,
        empty: insuredCustomerContactRef === null ? true : undefined,
      },
      insuranceSubscriptionStatusSearch: {
        allVisibleToCustomers: true,
        anyStatusList: [InsuranceSubscriptionStatus.ACTIVE, InsuranceSubscriptionStatus.DISCOUNTING],
      },
      subscriptionSearch: {
        archived: false,
        customerSearch: {
          exactCustomerRef: customerRef,
        },
      },
    };
    return insuranceSubscriptionSearch;
  }

  private createContactPreviewModelsFromGroupSummaries$(customerRef: Ref<Customer>,
                                                        groupField: InsuranceSubscriptionGroupField,
                                                        groupSummaries: SearchResult<InsuranceSubscriptionGroupSummary>,
                                                        mainCustomerContactRef: Ref<CustomerContact>): Observable<ContactPensionProductsPreviewModel[]> {
    if (groupField === InsuranceSubscriptionGroupField.CUSTOMER) {
      return this.createCustomerGroups(groupSummaries);
    } else if (groupField === InsuranceSubscriptionGroupField.INSURED_CONTACT) {
      return this.createInsuredContactsGroups(groupSummaries, customerRef, mainCustomerContactRef);
    } else {
      throw new Error(`Unhandled group field ${groupField}`);
    }
  }

  private createInsuredContactsGroups(groupSummaries: SearchResult<InsuranceSubscriptionGroupSummary>, customerRef: Ref<Customer>, mainCustomerContactRef: Ref<CustomerContact>) {
    const definedContactRefs: Ref<CustomerContact>[] = groupSummaries.list
      // Only consider contacts with results
      .filter(g => g.groupCount > 0)
      // Ignore groups without any main contact
      .filter(g => g.groupId != null)
      .map(g => {
        return {id: g.groupId} as Ref<CustomerContact>;
      });
    // Fetch subscriptions for each group (re-including the undefined one)
    const allInsuredContactRefs = [null, ...definedContactRefs];
    // We use tuples(array of 2 elements) to group subscriptions by contact (with fallback value) before creating models
    const allTuples$List = allInsuredContactRefs.map(ref => this.searchContactSubscriptionsTuple$(customerRef, mainCustomerContactRef, ref));
    const allTuplesList$ = allTuples$List.length === 0 ? of([]) : forkJoin(allTuples$List);
    return allTuplesList$.pipe(
      map(list => list.reduce((a, b) => this.reducePerContact(a, b), [])),
      switchMap(tupleList => this.createTupleModels$(tupleList)),
    );
  }

  private createCustomerGroups(groupSummaries: SearchResult<InsuranceSubscriptionGroupSummary>) {
    if (groupSummaries == null || groupSummaries.count === 0) {
      return of([]);
    }
    // Should have 1 group
    console.assert(groupSummaries.count === 1);
    const singleGroup = groupSummaries.list[0];
    const customerRef: Ref<Customer> = {id: singleGroup.groupId};

    return this.searchALlSubscriptions$(customerRef).pipe(
      switchMap(r => this.createSingleGroupModel(r)),
      map(r => [r]),
    );
  }

  private searchContactSubscriptionsTuple$(customerRef: Ref<Customer>, mainCustomerContactRef: Ref<CustomerContact>,
                                           insuredCustomerCOntactRef: Ref<CustomerContact>): Observable<[Contact, SearchResult<InsuranceSubscription>]> {
    const effectiveCustomerContactRef = isValidRef(insuredCustomerCOntactRef) ? insuredCustomerCOntactRef : mainCustomerContactRef;
    // Search for this specific contact, or with this field empty otherwise
    const searchFilterCustomerContactRef = isValidRef(insuredCustomerCOntactRef) ? insuredCustomerCOntactRef : null;
    const insuranceSubscriptionSearchFilter = this.createInsuranceSubscriptionSearchFilter(customerRef, searchFilterCustomerContactRef);
    const pagination = SimplePaginationUtils.newPagination(50);
    const subscriptionResults$ = this.insuranceSubscriptionService.searchInsuranceSubscriptions$(insuranceSubscriptionSearchFilter, pagination);

    const contact$ = this.customerContactService.getCustomerContact$(effectiveCustomerContactRef).pipe(
      switchMap(cc => this.contactService.getContact$(cc.contactRef)),
    );

    return forkJoin([subscriptionResults$, contact$]).pipe(
      map(r => [r[1], r[0]]),
    );
  }


  private searchALlSubscriptions$(customerRef: Ref<Customer>): Observable<SearchResult<InsuranceSubscription>> {
    // Search for this specific contact, or with this field empty otherwise
    const insuranceSubscriptionSearchFilter = this.createInsuranceSubscriptionSearchFilterNoContact(customerRef);
    const pagination = SimplePaginationUtils.newPagination(300);
    const subscriptionResults$ = this.insuranceSubscriptionService.searchInsuranceSubscriptions$(insuranceSubscriptionSearchFilter, pagination);

    return subscriptionResults$;
  }

  private createTupleModels$(tupleList: [Contact, SearchResult<InsuranceSubscription>][]): Observable<ContactPensionProductsPreviewModel[]> {
    const model$List = tupleList.map(r => this.createContactPreviewModel$(r[0], r[1]));
    const modelList$ = model$List.length === 0 ? of([]) : forkJoin(model$List);
    return modelList$;
  }

  private createSingleGroupModel(results: SearchResult<InsuranceSubscription>): Observable<ContactPensionProductsPreviewModel> {
    return this.createCustomerPreviewModel$(results);
  }

  private reducePerContact(cur: [Contact, SearchResult<InsuranceSubscription>][], next: [Contact, SearchResult<InsuranceSubscription>]) {
    const existingIndex = cur.findIndex(tuple => isSameRef(tuple[0], next[0]));
    if (existingIndex >= 0) {
      const curTuple = cur[existingIndex];
      const curSearchResult = curTuple[1];
      const nextSearchResult = next[1];
      curSearchResult.list = [...curSearchResult.list, ...nextSearchResult.list];
      curSearchResult.count = curSearchResult.count + nextSearchResult.count;
      return cur;
    } else {
      return [...cur, next];
    }
  }

}
